import React, { useState, useCallback } from 'react';
import {
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  HelpOutline,
  CheckCircle,
  Error,
  AutoAwesome,
} from '@mui/icons-material';

interface SmartTextFieldProps {
  label: string;
  value?: string | number;
  onChange?: (value: string) => void;
  placeholder?: string;
  helpText?: string;
  examples?: string[];
  suggestions?: string[];
  validation?: {
    required?: boolean;
    pattern?: RegExp;
    message?: string;
  };
  error?: boolean;
  errorMessage?: string;
  multiline?: boolean;
  rows?: number;
  type?: string;
  autoSuggest?: boolean;
  [key: string]: any;
}

const SmartTextField: React.FC<SmartTextFieldProps> = ({
  label,
  value = '',
  onChange,
  placeholder,
  helpText,
  examples = [],
  suggestions = [],
  validation,
  error = false,
  errorMessage,
  multiline = false,
  rows = 1,
  type = 'text',
  autoSuggest = false,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange?.(newValue);

    // Real-time validation
    if (validation && newValue) {
      if (validation.pattern) {
        setIsValid(validation.pattern.test(newValue));
      } else if (validation.required) {
        setIsValid(newValue.length > 0);
      }
    } else {
      setIsValid(null);
    }
  }, [onChange, validation]);

  // Handle select all on focus
  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    if (autoSuggest && suggestions.length > 0) {
      setShowSuggestions(true);
    }
    // Select all text on focus for easy deletion
    event.target.select();
  }, [autoSuggest, suggestions.length]);

  const handleSuggestionClick = (suggestion: string) => {
    onChange?.(suggestion);
    setShowSuggestions(false);
  };

  const getValidationIcon = () => {
    if (error) return <Error color="error" />;
    if (isValid === true) return <CheckCircle color="success" />;
    if (isValid === false) return <Error color="error" />;
    return null;
  };

  const getHelperText = () => {
    if (error && errorMessage) return errorMessage;
    if (isValid === false && validation?.message) return validation.message;
    if (helpText) return helpText;
    if (examples.length > 0 && focused) {
      return `Příklad: ${examples[0]}`;
    }
    return '';
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        label={label}
        value={value?.toString() || ''}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={() => {
          setFocused(false);
          setTimeout(() => setShowSuggestions(false), 200); // Delay to allow suggestion clicks
        }}
        placeholder={placeholder || (examples.length > 0 ? `Příklad: ${examples[0]}` : '')}
        multiline={multiline}
        rows={rows}
        type={type}
        error={error || isValid === false}
        helperText={getHelperText()}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {getValidationIcon()}
              {helpText && (
                <Tooltip title={helpText} placement="top">
                  <IconButton size="small">
                    <HelpOutline fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: 'white',
            transition: 'all 0.3s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#000000',
              borderWidth: 2,
            },
            '&:hover': {
              backgroundColor: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#333333',
                borderWidth: 2,
              },
            },
            '&.Mui-focused': {
              backgroundColor: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e72f0d',
                borderWidth: 2,
              },
            },
            '&.Mui-error': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d32f2f',
                borderWidth: 2,
              },
            },
          },
          '& .MuiInputLabel-root': {
            fontWeight: 600,
            fontSize: '1rem',
            color: '#000000',
            '&.Mui-focused': {
              color: '#e72f0d',
              fontWeight: 600,
            },
            '&.Mui-error': {
              color: '#d32f2f',
            },
          },
          '& .MuiFormHelperText-root': {
            marginLeft: 2,
            marginTop: 1,
            fontSize: '0.8rem',
            fontWeight: 500,
            color: '#000000',
          },
          '& .MuiInputBase-input': {
            fontSize: '1rem',
            padding: '16px 14px',
            color: '#000000',
            '&::placeholder': {
              color: '#888888',
              opacity: 0.8,
              fontStyle: 'italic',
            },
            '&::selection': {
              backgroundColor: '#e72f0d',
              color: 'white',
            },
          },
        }}
        {...props}
      />

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.08)',
            maxHeight: 200,
            overflow: 'auto',
            mt: 1,
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome fontSize="small" sx={{ color: '#e72f0d' }} />
              <Typography variant="caption" sx={{ color: '#e72f0d' }} fontWeight={600}>
                Návrhy
              </Typography>
            </Box>
          </Box>
          {suggestions.map((suggestion, index) => (
            <Box
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
                '&:last-child': {
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                },
              }}
            >
              <Typography variant="body2">{suggestion}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Examples chips */}
      {examples.length > 1 && focused && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              mr: 1,
              color: '#808080',
              fontStyle: 'italic',
              fontSize: '0.8rem'
            }}
          >
            Příklady:
          </Typography>
          {examples.slice(0, 3).map((example, index) => (
            <Chip
              key={index}
              label={example}
              size="small"
              variant="outlined"
              onClick={() => onChange?.(example)}
              sx={{
                fontSize: '0.75rem',
                height: 24,
                cursor: 'pointer',
                backgroundColor: '#f0f0f0',
                border: '1px solid #d0d0d0',
                color: '#808080',
                fontStyle: 'italic',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                  borderColor: '#b0b0b0',
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SmartTextField;